/**
 * internal-app-platform 模块 - 中文翻译
 *
 * 仅 onboarding + 自助管理页面用，详见 docs/modules/internal-app-platform/05-ui-interaction-spec.md
 */
export const zh = {
  pageTitle: '我的 Apps',
  pageSubtitle: '全程通过 Claude Code 对话部署，本页只做接入与查看',

  // 区块 A：接入 Claude Code
  onboard: {
    sectionTitle: '1. 接入 Claude Code',
    statusNone: '尚未生成 token',
    statusActive: '✓ 有效（剩 {days} 天 / {date} 过期）',
    statusExpiring: '⚠ 即将过期（还有 {days} 天）',
    statusExpired: '✗ 已过期（{date}）',
    statusRevoked: '已撤销于 {date}',
    instructionHint:
      '在 shell 终端（不是 Claude Code 内）执行该命令，添加完后启动 Claude Code，用 `claude mcp list` 可看到 ffoa-apps。',
    copyMaskedHint:
      'token 明文不在页面显示，复制完整命令时会带上明文（仅本次生成可见）。',
    deployPromptTitle: '💡 部署话术（推荐复制使用）',
    deployPromptTemplate:
      '用 ffoa-apps MCP 的 deploy_prepare 工具，把这个项目部署到内部 app 平台，应用名叫 <在这里改成你想要的名字>。不要生成 Dockerfile、systemd、nginx 等配置文件，平台会自动处理。',
    deployPromptHint:
      '直接说"帮我部署"经常让 Claude 走通用路径（生成 Dockerfile / nginx / systemd 一堆没用的文件）。用这句模板能强制走 MCP，避免翻车。',
    toastDeployPromptCopied: '已复制部署话术到剪贴板',
    actions: {
      generate: '生成新 token',
      regenerate: '重新生成',
      revoke: '撤销',
      copyCommand: '复制完整命令',
      copyDeployPrompt: '复制部署话术',
      copied: '已复制',
    },
    regenerateConfirmTitle: '确认重新生成？',
    regenerateConfirmBody:
      '旧 token 会立即失效，已接入的 Claude Code 会话需在 shell 重新执行 `claude mcp add --transport http ...`。\n如果只是想在新设备接入，无需重新生成（同一 token 可多设备共用）。',
    regenerateConfirmCancel: '取消',
    regenerateConfirmSubmit: '确认重新生成',
    maskedCopied: '••••••••（已复制到剪贴板）',
    maskedCopyHint: '••••••••（点"复制完整命令"复制）',
    revokeConfirmTitle: '确认撤销 token？',
    revokeConfirmBody:
      '撤销后所有设备上的接入立即失效，需要在新设备上接入需要先生成新 token。',
    revokeConfirmInputLabel: '请输入「REVOKE」确认（不区分大小写也接受「撤销」）',
    revokeConfirmPlaceholder: 'REVOKE',
    revokeConfirmCancel: '取消',
    revokeConfirmSubmit: '确认撤销',
    toastIssued: '新 token 已生成，完整命令已复制到剪贴板。请立即粘贴使用。',
    toastRevoked: 'token 已撤销',
    toastCopied: '已复制完整命令到剪贴板',
    toastUrlCopied: '已复制 URL 到剪贴板',
    toastCopyFailed: '复制失败，请从上方命令框手动选中复制',
    toastError: '操作失败，请稍后重试',
    expiringWarning: 'token 即将过期，建议尽快重新生成。',
    expiredHint: 'token 已失效，请生成新 token 后重新接入。',
    revokedHint: 'token 已撤销，请生成新 token。',
  },

  // 区块 B：我已部署的 apps
  apps: {
    sectionTitle: '2. 我已部署的 apps',
    sectionTitleWithCount: '2. 我已部署的 apps（{count}）',
    empty: '还没有部署过 app',
    emptyHint: '完成上方接入后，在 Claude Code 里说"帮我部署 xxx 工具"即可',
    showDestroyed: '显示已销毁',
    hideDestroyed: '隐藏已销毁',
    columns: {
      slug: '名称',
      url: '访问 URL',
      status: '状态',
      lastDeployed: '最后部署',
      actions: '操作',
    },
    status: {
      PENDING: '准备中',
      BUILDING: '构建中',
      HEALTHY: '运行中',
      FAILED: '构建失败',
      DISABLED: 'IT 已暂停',
      DESTROYED: '已销毁',
      DISABLED_ARCHIVED: '已停用',
      PURGED: '已清理',
    },
    destroyedWithRecover: '已销毁 · 可恢复至 {date}',
    actions: {
      copyUrl: '复制 URL',
      open: '打开',
    },
    urlOffline: '— (URL 已下线)',
    destroyedAt: '销毁于 {date}',
    lastDeployedAt: '最后部署 {date}',
  },

  // 区块 C：使用指南（可展开）
  usageGuide: {
    sectionTitle: '使用指南 / FAQ',
    sectionHint: '第一次接入 / 不知道下一步说什么？点开看完整 walkthrough。',
    fullDocLink: '完整版（含截图）见 docs/modules/internal-app-platform/13-usage-guide.md',
    steps: [
      {
        title: '步骤 1 · 复制 mcp add 命令',
        body: '上方区块点 "生成新 token" / "重新生成"。token 明文只在"刚生成"那一次显示一次，点 "复制完整命令" 会把带明文的 claude mcp add ... 整段进剪贴板。',
      },
      {
        title: '步骤 2 · 在普通终端（不是 Claude Code 里）执行',
        body: '打开 bash / zsh / PowerShell，粘贴上一步的命令并回车。然后重启 Claude Code，执行 `claude mcp list`，看到 ffoa-apps ✓ Connected 即接通。',
      },
      {
        title: '步骤 3 · 用上方"部署话术"模板让 Claude 部署',
        body: '复制黄色 callout 里的话术，改 <app 名>，发给 Claude。Claude 会自动调 deploy_prepare → git push → 平台 webhook → 构建 → 上线（30 秒 – 5 分钟）。',
      },
      {
        title: '步骤 4 · 等 5 分钟，访问 URL',
        body: 'push 完成 ≠ 上线完成。等 5 分钟左右刷新本页"已部署 apps"区，看到 status=运行中就能点 "打开" 访问。还打不开？跟 Claude 说"看下日志"。',
      },
    ],
    faqTitle: '常见问题',
    faq: [
      {
        q: 'Claude 给我生成了 Dockerfile / nginx.conf / GitHub Actions',
        a: '没用部署话术模板，Claude 走了通用路径。让 Claude 删掉那些文件，回本页复制"部署话术"重发一遍。',
      },
      {
        q: 'claude mcp list 看不到 ffoa-apps',
        a: '90% 是把 mcp add 命令贴到了 Claude Code 内部，而不是普通 shell。回到 bash/zsh/PowerShell 重新执行。注意 \\ 续行别断。',
      },
      {
        q: '部署完 URL 立即打不开 / 502 / 自签证书警告',
        a: '前 5 分钟正常（webhook 构建中）。5 分钟后还不行，让 Claude "看下日志"，多半是 env 缺值（比如缺数据库连接串）。',
      },
      {
        q: '报 unsupported_runtime',
        a: '当前 MVP 只支持 Node（package.json + start script）和纯静态站（根目录 index.html）。Python/Go/Java/Docker 暂不支持。',
      },
      {
        q: '报 app_in_terminal_state',
        a: '这个 slug 之前销毁过，30 天保留期内不能复用。换一个 slug 重新部署。',
      },
      {
        q: '把 token 搞丢了怎么办',
        a: '没有"找回"。点 "重新生成"，旧 token 立刻失效，所有设备需要重新跑 claude mcp add。',
      },
    ],
    boundariesTitle: '边界（你不能做什么）',
    boundaries: [
      '不能改别人的 app（按工号物理隔离）',
      '不能用保留 slug（admin / api / www / mail / login 等）',
      '销毁的 app 30 天内可让 IT 恢复，30 天后物理 purge 不可恢复',
      '单个 app 固定 256MB 内存 / 0.5 CPU，超出会被 OOM kill（日志会显示 Killed）',
      '平台不提供持久化磁盘（无状态容器）；要存数据用公司 DB / 对象存储',
    ],
  },

  // 错误
  errors: {
    loadStatus: '加载 token 状态失败',
    loadApps: '加载 app 列表失败',
    retry: '重试',
  },
};
